import {
  DataLoaderParams,
  Network,
  NetworkAccountOption,
  NetworkDataLoaderParams,
  NetworkDomain,
  PaginatedResponse,
  QueryResponse
} from '@/types';
import gql from 'graphql-tag';
import { ApiService } from '@/services';

export class NetworkService extends ApiService {
  public async getNetworkAccountOptions(): Promise<NetworkAccountOption[]> {
    const query = gql`
      query networkAccountOptions {
        networkAccountOptions {
          id
          network_name
        }
      }
    `;

    return this.query<NetworkAccountOption[]>(
      { query },
      'networkAccountOptions'
    );
  }

  public async updateNetworkAccountOption(
    networkAccountOption: NetworkAccountOption
  ): Promise<NetworkAccountOption> {
    const mutation = gql`
      mutation updateNetworkOption(
        $networkAccountOption: NetworkAccountOptionInput
      ) {
        updateNetworkOption(networkAccountOption: $networkAccountOption) {
          id
          options {
            key
            value
            options
            title
            description
            help_text
            encrypted
          }
        }
      }
    `;

    return this.mutate(
      { mutation, variables: { networkAccountOption } },
      'updateNetworkOption'
    );
  }

  public async createNetworkDomain(
    domain: NetworkDomain
  ): Promise<NetworkDomain> {
    const mutation = gql`
      mutation createNetworkDomain($domain: AffiliateNetworkDomainInput) {
        createNetworkDomain(domain: $domain) {
          id
          domain
          affiliate_network_id
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { domain } },
      'createNetworkDomain'
    );
  }

  public async deleteNetworkDomain(id: number): Promise<NetworkDomain> {
    const mutation = gql`
      mutation deleteNetworkDomain($id: ID!) {
        deleteNetworkDomain(id: $id) {
          id
          domain
          affiliate_network_id
          created_at
          updated_at
        }
      }
    `;
    return this.mutate({ mutation, variables: { id } }, 'deleteNetworkDomain');
  }

  public async addMerchantToNetwork(merchantId: number, networkId: number) {
    const mutation = gql`
      mutation addMerchantToNetwork($merchantId: ID!, $networkId: ID!) {
        addMerchantToNetwork(
          merchant_id: $merchantId
          affiliate_network_id: $networkId
        ) {
          status
          message
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { merchantId, networkId } },
      'addMerchantToNetwork'
    );
  }

  public async removeMerchantFromNetwork(
    merchantId: number,
    networkId: number
  ) {
    const mutation = gql`
      mutation removeMerchantFromNetwork($merchantId: ID!, $networkId: ID!) {
        removeMerchantFromNetwork(
          merchant_id: $merchantId
          affiliate_network_id: $networkId
        ) {
          status
          message
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { merchantId, networkId } },
      'removeMerchantFromNetwork'
    );
  }

  public async getNetworks(
    params: NetworkDataLoaderParams
  ): Promise<QueryResponse<PaginatedResponse<Network>>> {
    const query = gql`
      query affiliateNetworks(
        $first: Int!
        $page: Int!
        $filters: NetworkDataLoaderParams
      ) {
        affiliateNetworks(first: $first, page: $page, filters: $filters) {
          data {
            id
            network_name
            acronym
            cookie_stuff
            affiliate_network_status
            merchant_count
            is_autoconfigured
          }
          paginatorInfo {
            total
            perPage
            currentPage
            hasMorePages
          }
        }
      }
    `;
    return this.query<QueryResponse<PaginatedResponse<Network>>>({
      query,
      variables: params
    });
  }

  public async searchNetworks(q: string): Promise<Network[]> {
    const query = gql`
      query searchAffiliateNetworks($search: String) {
        searchAffiliateNetworks(search: $search) {
          id
          network_name
        }
      }
    `;
    return this.query<Network[]>(
      { query, variables: { search: q } },
      'searchAffiliateNetworks'
    );
  }

  public async getNetwork(params: NetworkDataLoaderParams): Promise<Network> {
    const query = gql`
      query affiliateNetwork($id: ID!) {
        affiliateNetwork(id: $id) {
          id
          network_name
          acronym
          cookie_stuff
          affiliate_network_status
          country_code
          templates {
            id
            template
            status
            affiliate_network {
              id
            }
          }
          merchants {
            id
            merchant_name
          }
          domains {
            id
            domain
          }
          network_account_option {
            id
            options {
              key
              value
              options
              title
              description
              help_text
              encrypted
            }
          }
          login_url
          affiliate_code_description
          network_account_id
          is_autoconfigured
          created_at
          updated_at
        }
      }
    `;
    return this.query(
      {
        query,
        variables: { id: params.filters.networkId }
      },
      'affiliateNetwork'
    );
  }

  public async mutateNetwork(network: Network): Promise<Network> {
    return network.id
      ? this.updateNetwork(network)
      : this.createNetwork(network);
  }

  public async updateNetwork(network: Network): Promise<Network> {
    const mutation = gql`
      mutation updateAffiliateNetwork($network: AffiliateNetworkInput) {
        updateAffiliateNetwork(network: $network) {
          id
          network_name
          acronym
          cookie_stuff
          affiliate_network_status
          country_code
          templates {
            id
            template
            status
            affiliate_network {
              id
            }
          }
          merchants {
            id
            merchant_name
          }
          domains {
            id
            domain
          }
          network_account_option {
            id
            options {
              key
              value
              options
              title
              description
              help_text
              encrypted
            }
          }
          login_url
          affiliate_code_description
          network_account_id
          is_autoconfigured
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { network } },
      'updateAffiliateNetwork'
    );
  }

  public async createNetwork(network: Network): Promise<Network> {
    const mutation = gql`
      mutation createAffiliateNetwork($network: AffiliateNetworkInput) {
        createAffiliateNetwork(network: $network) {
          id
          network_name
          acronym
          cookie_stuff
          affiliate_network_status
          country_code
          templates {
            id
            template
            status
            affiliate_network {
              id
            }
          }
          merchants {
            id
            merchant_name
          }
          domains {
            id
            domain
          }
          network_account_option {
            id
            options {
              key
              value
              options
              title
              description
              help_text
              encrypted
            }
          }
          login_url
          affiliate_code_description
          network_account_id
          is_autoconfigured
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { network } },
      'createAffiliateNetwork'
    );
  }

  public async deleteNetwork(id: number): Promise<Network> {
    const mutation = gql`
      mutation deleteAffiliateNetwork($id: ID!) {
        deleteAffiliateNetwork(id: $id) {
          id
          network_name
          acronym
          country_code
          cookie_stuff
          affiliate_network_status
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { id } },
      'deleteAffiliateNetwork'
    );
  }
}

export const networkSvc: NetworkService = new NetworkService();
